import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';

const CVItemDialog = ({ open, onClose, item, sectionId, sections, onSave }) => {
  const [formData, setFormData] = useState({
    section_id: '',
    title: '',
    subtitle: '',
    description: '',
    start_date: null,
    end_date: null,
    location: '',
    order_index: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        section_id: item.section_id || '',
        title: item.title || '',
        subtitle: item.subtitle || '',
        description: item.description || '',
        start_date: item.start_date ? dayjs(item.start_date) : null,
        end_date: item.end_date ? (item.end_date.toLowerCase() === 'nu' ? 'nu' : dayjs(item.end_date)) : null,
        location: item.location || '',
        order_index: item.order_index || 0,
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    } else {
      setFormData({
        section_id: sectionId || '',
        title: '',
        subtitle: '',
        description: '',
        start_date: null,
        end_date: null,
        location: '',
        order_index: 0,
        is_active: true
      });
    }
    setError('');
  }, [item, sectionId, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? formData.start_date.format('YYYY-MM-DD') : null,
        end_date: formData.end_date ? (formData.end_date === 'nu' ? 'nu' : formData.end_date.format('YYYY-MM-DD')) : null
      };

      if (item) {
        // Update existing item
        await axios.put(`/api/cv/admin/items/${item.id}`, submitData);
      } else {
        // Create new item
        await axios.post('/api/cv/admin/items', submitData);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
      setError(error.response?.data?.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field) => (date) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {item ? 'Edit CV Item' : 'Add New CV Item'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={formData.section_id}
                    onChange={handleInputChange('section_id')}
                    required
                    disabled={loading}
                  >
                    {sections.map((section) => (
                      <MenuItem key={section.id} value={section.id}>
                        {section.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Title"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Subtitle (e.g., Company, Institution)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formData.subtitle}
                  onChange={handleInputChange('subtitle')}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Description"
                  type="text"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={handleDateChange('start_date')}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'dense'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <DatePicker
                  label="End Date (leave empty for current)"
                  value={formData.end_date}
                  onChange={handleDateChange('end_date')}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'dense'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={8}>
                <TextField
                  margin="dense"
                  label="Location"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={4}>
                <TextField
                  margin="dense"
                  label="Order Index"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formData.order_index}
                  onChange={handleInputChange('order_index')}
                  disabled={loading}
                  helperText="Lower = first"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleInputChange('is_active')}
                      disabled={loading}
                    />
                  }
                  label="Active (visible on CV)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CVItemDialog;